import { AttachFile } from '@material-ui/icons';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import * as _ from 'lodash';
import { action, observable, runInAction } from 'mobx';
import React from 'react';
import ObservationsLogic from 'src/chevrons/behaviors/observations/ObservationsLogic';
import { IColumnSetting } from 'src/common/ESDataTable';
import ITableOptions from 'src/common/ITableOptions';
import { DefaultObservation, IObservation } from 'src/Definitions';
import {
    Client,
    IObservationDto,
    ObservationAttachmentFileDto,
    ObservationDto,
    SwaggerResponse,
} from 'src/generated_client';
import GlobalStore from 'src/stores/GlobalStore';
import DataTableStore from '../DataTableStore';

const ColumnSettingsDto = 'ObservationDto';

export default class ObservationsStore {
    @observable
    public isLoading: boolean = false;
    @observable
    public isCarouselOpen: boolean;
    @observable
    public carouselAttachmentFiles: ObservationAttachmentFileDto[];
    @observable
    public observations: IObservation[];
    @observable
    public selectedObservation?: IObservation;
    public dataTableStore: DataTableStore<IObservationDto, IObservation>;
    private _logic: ObservationsLogic = new ObservationsLogic();
    private scrollPage?: number;

    constructor(
        private globalStore: GlobalStore,
        private client: Client,
        private behaviorId: string
    ) {
        this.dataTableStore = new DataTableStore<IObservationDto, IObservation>(
            globalStore,
            ({ types, filter, includeAllObservers, page, orderBy, includeInactives }) =>
                client.getAllObservations(
                    this.behaviorId,
                    types,
                    filter,
                    includeAllObservers,
                    page,
                    undefined,
                    orderBy,
                    includeInactives
                ),
            (observation) => this.setupObservations(observation),
            () => {}
        );
    }

    @action.bound
    public toggleCarousel() {
        if (this.isCarouselOpen) {
            this.isCarouselOpen = false;
        } else if (
            this.selectedObservation &&
            this.selectedObservation.observationAttachmentFiles
        ) {
            this.isCarouselOpen = true;
            this.carouselAttachmentFiles = _.cloneDeep(
                this.selectedObservation.observationAttachmentFiles
            );
        }
    }

    @action.bound
    public disableObservation(observation: IObservation) {
        this.isLoading = true;
        return this.client
            .archiveObservation(observation.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public getColumnSettingsAndObservations(
        typeFilter?: string,
        includeAllObservers?: boolean,
        thenFunction?: () => void
    ) {
        if (!this.globalStore.user) {
            return;
        }
        this.isLoading = true;
        return this.client
            .apiColumnSettingsByUserIdByTypeGet(this.globalStore.user.id, ColumnSettingsDto)
            .then((data) => {
                runInAction(() => {
                    this.dataTableStore.setColumns(data.result);
                    const sortedColumn = _.find(this.dataTableStore.columns, (col) => !!col.sort);
                    if (sortedColumn) {
                        this.dataTableStore.setTableOrderBy(sortedColumn.sort);
                    }
                    this.loadData(typeFilter, includeAllObservers);
                });
            })
            .then(thenFunction)
            .catch(() => {})
            .finally(() => runInAction(() => (this.isLoading = false)));
    }

    private loadData(typeFilter = '', includeAllObservers = true as boolean) {
        const tableOptions = {
            filter: '',
            includeAllObservers,
            includeInactives: false,
            page: 1,
            showCompleted: false,
            types: typeFilter,
        } as ITableOptions;
        this.dataTableStore.updateTableOptions(tableOptions);
        this.addTypeIcon();
    }

    private addTypeIcon() {
        this.dataTableStore.columns.unshift(
            {
                columnName: 'Type',
                fieldName: 'icon',
                isVisible: true,
                type: 'React.Component',
            } as IColumnSetting,
            {
                columnName: 'Attachments',
                fieldName: 'hasFilesIcon',
                isVisible: true,
                type: 'React.Component',
            } as IColumnSetting
        );
    }

    @action.bound
    public getAllObservations(pageNumber?: number, pageSize?: number) {
        if (!pageNumber) {
            pageNumber = 1;
        }
        if (!pageSize) {
            pageSize = 25;
        }
        this.isLoading = true;
        return this.client
            .getAllObservations(undefined, undefined, undefined, undefined, pageNumber, pageSize)
            .then((resp: SwaggerResponse<IObservationDto[]>) =>
                runInAction(() => {
                    this.observations = this.setupObservations(resp.result);
                    this.isLoading = false;
                })
            );
    }

    @action.bound
    public getObservationsByAssessmentId(
        assessmentId: string,
        unattachedToClusterOnly?: boolean,
        pageNumber?: number,
        pageSize?: number
    ) {
        this.isLoading = true;
        return this.client
            .getObservationsByAssessmentId(
                assessmentId,
                undefined,
                undefined,
                unattachedToClusterOnly,
                pageNumber,
                pageSize
            )
            .then((resp: SwaggerResponse<IObservationDto[]>) => {
                runInAction(() => {
                    if (pageNumber! > 1) {
                        this.observations = this.observations.concat(
                            this.setupObservations(resp.result, false)
                        );
                    } else {
                        this.scrollPage = pageNumber;
                        this.observations = _.sortBy(this.setupObservations(resp.result, false), [
                            'typeName',
                            'createdOn',
                        ]);
                    }
                    this.isLoading = false;
                });
            });
    }

    @action.bound
    public getNextObservationsByAssessmentPage(
        assessmentId: string,
        pageSize: number,
        unattachedToClusterOnly?: boolean
    ) {
        if (this.scrollPage && this.scrollPage! * pageSize! <= this.observations.length) {
            ++this.scrollPage!;
            return this.getObservationsByAssessmentId(
                assessmentId,
                unattachedToClusterOnly,
                this.scrollPage,
                pageSize
            );
        } else {
            return Promise.resolve();
        }
    }

    @action.bound
    public setSelectedObservation(observation: IObservation) {
        if (this.selectedObservation) {
            this.selectedObservation.isModified = false;
        }
        this.selectedObservation = observation;
        this.carouselAttachmentFiles = observation.observationAttachmentFiles!;
    }

    @action.bound
    public resetSelectedObservation() {
        if (this.selectedObservation) {
            this.selectedObservation.isModified = false;
        }
        this.selectedObservation = undefined;
        this.carouselAttachmentFiles = [];
    }

    @action.bound
    public recoverObservation(observation: IObservation) {
        this.isLoading = true;
        return this.client
            .reactivateObservation(observation.id)
            .then(() => this.dataTableStore.loadData())
            .catch(() => {})
            .finally(() => {
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    private resetUIState(shouldClearFilter: boolean, shouldResetUIState: boolean = true) {
        if (shouldClearFilter) {
            this.dataTableStore.clearFilter();
        }
        if (shouldResetUIState) {
            this.dataTableStore.loadData();
        }
    }

    @action.bound
    public saveObservation(observation: IObservation, shouldResetUIState: boolean = true) {
        this.isLoading = true;
        let promise;
        observation.isModified = false;
        const observationDto = new ObservationDto(observation);
        observationDto.observationAttachmentFiles = this.carouselAttachmentFiles;
        if (observation.isNew) {
            promise = this.client.createObservation(observationDto);
        } else {
            promise = this.client.updateObservation(observation.id, observationDto);
        }
        return promise
            .then(() => {
                if (this.carouselAttachmentFiles) {
                    this.carouselAttachmentFiles.forEach((observationFile) => {
                        this.client
                            .uploadAttachmentFile(observationFile.attachmentFile, 'Observations')
                            .then(() => {
                                this.resetUIState(observation.isNew, shouldResetUIState);
                            })
                            .catch(() => {})
                            .finally(() => runInAction(() => {}));
                    });
                } else {
                    this.resetUIState(observation.isNew, shouldResetUIState);
                }
            })
            .catch(() => {})
            .finally(() =>
                runInAction(() => {
                    this.isLoading = false;
                })
            );
    }

    private setupObservations = (
        observations: IObservationDto[],
        shouldSetupCustomFields: boolean = true
    ) => {
        if (shouldSetupCustomFields) {
            return observations.map((x) =>
                this.customizeObservationRecord(this.addDefaultFields(x))
            );
        } else {
            return observations.map((x) => this.addDefaultFields(x));
        }
    };

    private addDefaultFields = (observation: IObservationDto): IObservation => {
        return { ...DefaultObservation(), ...observation };
    };

    private customizeObservationRecord = (observation: IObservation) => {
        observation.icon = this.getObservationIcon(observation);

        if (observation.observationAttachmentFiles) {
            observation.hasFilesIcon =
                observation.observationAttachmentFiles.length > 0 ? <AttachFile /> : undefined;
        } else {
            observation.hasFilesIcon = undefined;
        }

        return this.setupObservationMenuItems(observation);
    };

    private setupObservationMenuItems = (observation: IObservation) => {
        observation.menuItems = [];
        observation.menuItems.push({
            isConfirm: true,
            name: 'Delete',
            onClick: () => this.disableObservation(observation),
        });
        return observation;
    };

    @action.bound
    public getObservationIcon = (observation: IObservation) => {
        return (
            <FiberManualRecordIcon
                nativeColor={this._logic.getColorFromObservationType(observation.typeName!)}
            />
        );
    };
}



// WEBPACK FOOTER //
// ./src/stores/ObservationsStore/ObservationsStore.tsx